import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isScrollingUp) {
        if (this.getBoundingClientRect().top >= 0) {
          this.dataset.scrollDirection = 'none';
        } else {
          this.dataset.scrollDirection = 'up';
        }
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  // Search is never auto-open; user must click magnifying glass to reveal
  const alwaysVisibleOnMobile = false;
  let ignoreMobileScrollUntil = 0;

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }

  // Header search row toggling (in-flow)
  const searchRow = header?.querySelector('.header__row--search');
  /** @type {HTMLInputElement | null} */
  const searchInput = searchRow ? searchRow.querySelector('.search-form__input') : null;

  const openSearch = () => {
    if (!header || !searchRow) return;
    header.classList.add('is-search-open');
    searchRow.removeAttribute('inert');
    searchRow.setAttribute('aria-hidden', 'false');
    // Recalculate header group height after DOM change
    try { calculateHeaderGroupHeight?.(header, headerGroup); } catch {}
    setTimeout(() => searchInput && searchInput.focus(), 120);
    // Finalize height after transition
    setTimeout(() => { try { calculateHeaderGroupHeight?.(header, headerGroup); } catch {} }, 360);
    // Ignore initial layout-induced scrolls on mobile after open
    ignoreMobileScrollUntil = Date.now() + 500;
  };

  const closeSearch = () => {
    if (!header || !searchRow) return;
    header.classList.remove('is-search-open');
    searchRow.setAttribute('inert', '');
    searchRow.setAttribute('aria-hidden', 'true');
    try { calculateHeaderGroupHeight?.(header, headerGroup); } catch {}
    setTimeout(() => { try { calculateHeaderGroupHeight?.(header, headerGroup); } catch {} }, 360);
  };

  // Toggle by clicking any search toggle button rendered by snippet
  document.addEventListener('click', (e) => {
    const target = e.target instanceof Element ? e.target : null;
    const toggle = target?.closest('[data-header-search-toggle]');
    const closeBtn = target?.closest('[data-header-search-close]');

    if (toggle) {
      e.preventDefault();
      // Ignore toggle on mobile when search is always visible
      if (mobileQuery.matches && alwaysVisibleOnMobile) return;
      if (header?.classList.contains('is-search-open')) {
        closeSearch();
      } else {
        openSearch();
      }
    }

    if (closeBtn) {
      e.preventDefault();
      // Ignore close on mobile when search is always visible
      if (mobileQuery.matches && alwaysVisibleOnMobile) return;
      closeSearch();
    }
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && header?.classList.contains('is-search-open')) {
      closeSearch();
    }
  });

  // Optional: click outside header to close
  document.addEventListener('click', (e) => {
    if (!header?.classList.contains('is-search-open')) return;
    const target = e.target instanceof Element ? e.target : null;
    if (target && !header.contains(target)) {
      closeSearch();
    }
  });

  // Auto-close on mobile scroll (disabled for always-visible mobile)
  document.addEventListener('scroll', () => {
    if (!header?.classList.contains('is-search-open')) return;
    if (!mobileQuery.matches) return;
    if (alwaysVisibleOnMobile) return;
    if (Date.now() < ignoreMobileScrollUntil) return;
    closeSearch();
  }, { passive: true });

  // Ensure search is open by default on mobile and reacts to breakpoint changes
  const ensureMobileOpenState = () => {
    if (!header || !searchRow) return;
    if (mobileQuery.matches && alwaysVisibleOnMobile) {
      if (!header.classList.contains('is-search-open')) openSearch();
    } else if (!mobileQuery.matches) {
      // On desktop/tablet, keep default closed unless explicitly toggled
      closeSearch();
    }
  };

  ensureMobileOpenState();
  // Watch for viewport breakpoint changes
  if (typeof mobileQuery.addEventListener === 'function') {
    mobileQuery.addEventListener('change', ensureMobileOpenState);
  } else if (typeof mobileQuery.addListener === 'function') {
    // Safari fallback
    mobileQuery.addListener(ensureMobileOpenState);
  }
});
